import { BASE_CURRENCY, formatMoney, type Pesewas } from '@/lib/money'
import { cn } from '@/lib/utils'

export interface MoneyDisplayProps {
  /** Integer minor units, as the API serves them. `null` renders an em dash, not a zero. */
  value: Pesewas | null | undefined
  currency?: string
  /** Hide the currency symbol where a column header already carries it. */
  symbol?: boolean
  /** Accounting style shows negatives as (1,234.56) — used on statements and the trial balance. */
  negativeStyle?: 'minus' | 'parentheses'
  /** Show a leading + on positive amounts, for movement and variance columns. */
  signed?: boolean
  /** Colour negatives with the danger token. Off by default: a credit balance is not an alarm. */
  colourNegative?: boolean
  emphasis?: 'normal' | 'strong' | 'muted'
  className?: string
}

/**
 * Renders an amount held in integer pesewas.
 *
 * Figures are right-aligned with tabular numerals so a column of amounts lines up on the decimal
 * point — the difference between GH₵ 1,200.00 and GH₵ 120.00 has to be visible at a glance while
 * scanning a day's postings, not something the eye has to count digits for.
 */
export function MoneyDisplay({
  value,
  currency = BASE_CURRENCY,
  symbol = true,
  negativeStyle = 'minus',
  signed = false,
  colourNegative = false,
  emphasis = 'normal',
  className,
}: MoneyDisplayProps) {
  if (value === null || value === undefined) {
    return (
      <span className={cn('block text-right text-muted tabular-nums', className)} aria-label="No amount">
        —
      </span>
    )
  }

  const isNegative = value < 0

  return (
    <span
      className={cn(
        'numeric block text-right whitespace-nowrap tabular-nums',
        emphasis === 'strong' && 'font-semibold text-heading',
        emphasis === 'muted' && 'text-muted',
        colourNegative && isNegative && 'text-danger-text',
        className,
      )}
    >
      {formatMoney(value, { currency, symbol, negativeStyle, signed })}
    </span>
  )
}
